// ======================== GESTION DES PRODUITS - Unifié avec POS ========================
// Utilise pos-products avec liaison inventaire et recettes
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Package,
    Coffee,
    Wine,
    Cake,
    Soup,
    UtensilsCrossed,
    Sun,
    Check,
    X,
    RefreshCw,
    ArrowLeft,
    ChefHat,
    Link2,
    Unlink,
    AlertTriangle,
    PackageCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

// Catégories disponibles
const CATEGORIES = [
    { id: 'petit_dejeuner', name: 'Petit-déjeuner', icon: Sun },
    { id: 'plats', name: 'Plats', icon: Soup },
    { id: 'desserts', name: 'Desserts', icon: Cake },
    { id: 'boissons', name: 'Boissons', icon: Wine },
    { id: 'cafe', name: 'Café/Thé', icon: Coffee },
    { id: 'menu', name: 'Menus/Formules', icon: UtensilsCrossed },
    { id: 'supplements', name: 'Suppléments', icon: Plus },
    { id: 'autres', name: 'Autres', icon: Package },
];

const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || { name: categoryId || 'Autres', icon: Package };
};

const formatCFA = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR').format(Math.round(amount)) + ' F';
};

const MenuProductsPage = () => {
    const { api, hasPermission } = useAuth();
    
    // States
    const [products, setProducts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [inventoryProducts, setInventoryProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    
    // Dialog states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productToDelete, setProductToDelete] = useState(null);
    
    // Form state
    const [form, setForm] = useState({
        name: '',
        category: 'plats',
        price: '',
        linked_recipe_id: '',
        linked_product_id: '',
        stock_deduction_qty: '1',
        is_active: true
    });
    
    const canManage = hasPermission?.('manage_menu') ?? true;

    // Fetch all data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [productsRes, recipesRes, inventoryRes] = await Promise.all([
                api().get('/pos/products'),
                api().get('/recipes').catch(() => ({ data: [] })),
                api().get('/products').catch(() => ({ data: [] }))
            ]);
            setProducts(productsRes.data || []);
            setRecipes(recipesRes.data || []);
            setInventoryProducts(inventoryRes.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Get linked info for a product
    const getLinkedInfo = (product) => {
        if (product.linked_recipe_id) {
            const recipe = recipes.find(r => r.id === product.linked_recipe_id);
            return {
                type: 'recipe',
                name: recipe?.name || product.linked_recipe_name || 'Recette',
                cost: recipe?.cost_price_cfa || 0,
                margin: recipe?.margin_percent || 0,
                ingredients: recipe?.ingredients || []
            };
        }
        if (product.linked_product_id) {
            const inv = inventoryProducts.find(p => p.id === product.linked_product_id);
            return {
                type: 'inventory',
                name: inv?.name || product.linked_product_name || 'Inventaire',
                cost: inv?.cost_per_base_unit_cfa || inv?.purchase_price_cfa || 0,
                stock: inv?.stock_display || 'N/A'
            };
        }
        return null;
    };

    // Filter products
    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    // Group by category
    const groupedProducts = filteredProducts.reduce((acc, product) => {
        const cat = product.category || 'autres';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(product);
        return acc;
    }, {});

    // Open dialog for new product
    const handleNewProduct = () => {
        setEditingProduct(null);
        setForm({
            name: '',
            category: 'plats',
            price: '',
            linked_recipe_id: '',
            linked_product_id: '',
            stock_deduction_qty: '1',
            is_active: true,
            // Stock fields
            auto_create_inventory: true,
            initial_stock: '',
            stock_unit: 'unité',
            alert_threshold: '5',
            purchase_price: ''
        });
        setDialogOpen(true);
    };

    // Open dialog for editing
    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setForm({
            name: product.name || '',
            category: product.category || 'plats',
            price: (product.price_cfa || product.price || '').toString(),
            linked_recipe_id: product.linked_recipe_id || '',
            linked_product_id: product.linked_product_id || '',
            stock_deduction_qty: (product.stock_deduction_qty || 1).toString(),
            is_active: product.is_active !== false,
            // Stock fields
            auto_create_inventory: false,
            initial_stock: '',
            stock_unit: 'unité',
            alert_threshold: '5',
            purchase_price: ''
        });
        setDialogOpen(true);
    };

    // Handle form change
    const handleFormChange = (field, value) => {
        const newForm = { ...form, [field]: value };
        
        // If linking to recipe, clear product link and vice versa
        if (field === 'linked_recipe_id' && value) {
            newForm.linked_product_id = '';
            // Auto-fill price from recipe
            const recipe = recipes.find(r => r.id === value);
            if (recipe && !newForm.price) {
                newForm.price = (recipe.sale_price_cfa || '').toString();
            }
        }
        if (field === 'linked_product_id' && value) {
            newForm.linked_recipe_id = '';
        }
        
        setForm(newForm);
    };

    // Save product
    const handleSaveProduct = async () => {
        if (!form.name.trim()) {
            toast.error('Le nom du produit est requis');
            return;
        }
        
        const price = parseFloat(form.price) || 0;
        if (price <= 0) {
            toast.error('Le prix doit être supérieur à 0');
            return;
        }

        const payload = {
            name: form.name.trim(),
            category: form.category,
            price: price,
            linked_recipe_id: form.linked_recipe_id || null,
            linked_product_id: form.linked_product_id || null,
            stock_deduction_qty: parseFloat(form.stock_deduction_qty) || 1,
            is_active: form.is_active,
            // Stock fields (only for new products)
            auto_create_inventory: !editingProduct && form.auto_create_inventory && !form.linked_recipe_id && !form.linked_product_id,
            initial_stock: parseFloat(form.initial_stock) || 0,
            stock_unit: form.stock_unit || 'unité',
            alert_threshold: parseFloat(form.alert_threshold) || 5,
            purchase_price: parseFloat(form.purchase_price) || 0
        };

        try {
            if (editingProduct) {
                await api().put(`/pos/products/${editingProduct.id}`, payload);
                toast.success('Produit modifié avec succès');
            } else {
                await api().post('/pos/products', payload);
                toast.success('Produit créé avec succès');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    // Toggle availability
    const handleToggleAvailability = async (product) => {
        try {
            await api().put(`/pos/products/${product.id}`, {
                is_active: !product.is_active
            });
            toast.success(product.is_active ? 'Produit désactivé' : 'Produit activé');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la modification');
        }
    };

    // Delete product
    const handleDeleteProduct = async () => {
        if (!productToDelete) return;
        
        try {
            await api().delete(`/pos/products/${productToDelete.id}`);
            toast.success('Produit supprimé');
            setDeleteDialogOpen(false);
            setProductToDelete(null);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Sync all products to inventory
    const handleSyncInventory = async () => {
        try {
            const res = await api().post('/pos/products/sync-inventory');
            toast.success(`${res.data.synced} produits synchronisés avec l'inventaire`);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la synchronisation');
        }
    };

    // Get categories with counts
    const categoriesWithCounts = CATEGORIES.map(cat => ({
        ...cat,
        count: products.filter(p => p.category === cat.id).length
    })).filter(cat => cat.count > 0);

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <Link to="/dashboard">
                        <Button variant="ghost" size="sm">
                            <ArrowLeft className="w-4 h-4" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Package className="w-6 h-6 text-green-600" />
                            Gestion des Produits
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Produits de vente connectés à l'inventaire et aux recettes
                        </p>
                    </div>
                </div>
                
                {canManage && (
                    <div className="flex gap-2">
                        {products.filter(p => !p.linked_recipe_id && !p.linked_product_id).length > 0 && (
                            <Button variant="outline" onClick={handleSyncInventory}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Synchroniser ({products.filter(p => !p.linked_recipe_id && !p.linked_product_id).length})
                            </Button>
                        )}
                        <Button onClick={handleNewProduct} className="bg-green-600 hover:bg-green-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau produit
                        </Button>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <Package className="w-8 h-8 text-blue-500" />
                        <div>
                            <p className="text-2xl font-bold">{products.length}</p>
                            <p className="text-xs text-muted-foreground">Produits</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <ChefHat className="w-8 h-8 text-green-500" />
                        <div>
                            <p className="text-2xl font-bold">
                                {products.filter(p => p.linked_recipe_id).length}
                            </p>
                            <p className="text-xs text-muted-foreground">Liés recettes</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <Link2 className="w-8 h-8 text-purple-500" />
                        <div>
                            <p className="text-2xl font-bold">
                                {products.filter(p => p.linked_product_id).length}
                            </p>
                            <p className="text-xs text-muted-foreground">Liés inventaire</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <Unlink className="w-8 h-8 text-orange-500" />
                        <div>
                            <p className="text-2xl font-bold">
                                {products.filter(p => !p.linked_recipe_id && !p.linked_product_id).length}
                            </p>
                            <p className="text-xs text-muted-foreground">Non liés</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Rechercher un produit..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Toutes ({products.length})</SelectItem>
                        {categoriesWithCounts.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                                {cat.name} ({cat.count})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4" />
                </Button>
            </div>

            {/* Products List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            ) : filteredProducts.length === 0 ? (
                <Card className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun produit trouvé</p>
                    {canManage && (
                        <Button onClick={handleNewProduct} className="mt-4">
                            <Plus className="w-4 h-4 mr-2" />
                            Créer le premier produit
                        </Button>
                    )}
                </Card>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedProducts).map(([categoryId, categoryProducts]) => {
                        const catInfo = getCategoryInfo(categoryId);
                        const CatIcon = catInfo.icon;
                        
                        return (
                            <Card key={categoryId}>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <CatIcon className="w-5 h-5 text-green-600" />
                                        {catInfo.name}
                                        <Badge variant="secondary" className="ml-2">
                                            {categoryProducts.length}
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0 overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[200px]">Produit</TableHead>
                                                <TableHead>Liaison</TableHead>
                                                <TableHead className="text-right">Coût</TableHead>
                                                <TableHead className="text-right">Prix</TableHead>
                                                <TableHead className="text-right">Marge</TableHead>
                                                <TableHead className="text-center">Stock</TableHead>
                                                <TableHead className="text-center">Actif</TableHead>
                                                {canManage && <TableHead className="text-right">Actions</TableHead>}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {categoryProducts.map(product => {
                                                const linked = getLinkedInfo(product);
                                                const price = product.price_cfa || product.price || 0;
                                                const cost = linked?.cost || 0;
                                                const margin = price > 0 ? ((price - cost) / price * 100) : 0;
                                                
                                                return (
                                                    <TableRow 
                                                        key={product.id}
                                                        className={cn(!product.is_active && 'opacity-50 bg-gray-50')}
                                                    >
                                                        <TableCell>
                                                            <p className="font-medium">{product.name}</p>
                                                        </TableCell>
                                                        <TableCell>
                                                            {linked ? (
                                                                <div className="flex items-center gap-2">
                                                                    {linked.type === 'recipe' ? (
                                                                        <Badge className="bg-green-100 text-green-700 text-xs">
                                                                            <ChefHat className="w-3 h-3 mr-1" />
                                                                            Recette
                                                                        </Badge>
                                                                    ) : (
                                                                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                                                                            <PackageCheck className="w-3 h-3 mr-1" />
                                                                            Inventaire
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                                                                    <Unlink className="w-3 h-3 mr-1" />
                                                                    Non lié
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {cost > 0 ? formatCFA(cost) : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-right font-semibold text-green-600">
                                                            {formatCFA(price)}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {cost > 0 ? (
                                                                <Badge className={cn(
                                                                    "text-xs",
                                                                    margin >= 50 ? "bg-green-100 text-green-700" :
                                                                    margin >= 30 ? "bg-yellow-100 text-yellow-700" :
                                                                    "bg-red-100 text-red-700"
                                                                )}>
                                                                    {margin.toFixed(0)}%
                                                                </Badge>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            {linked?.type === 'inventory' ? (
                                                                <span className="text-sm">{linked.stock}</span>
                                                            ) : linked?.type === 'recipe' ? (
                                                                <span className="text-xs text-gray-500">
                                                                    {linked.ingredients?.length || 0} ing.
                                                                </span>
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <Switch
                                                                checked={product.is_active !== false}
                                                                onCheckedChange={() => handleToggleAvailability(product)}
                                                                disabled={!canManage}
                                                            />
                                                        </TableCell>
                                                        {canManage && (
                                                            <TableCell className="text-right">
                                                                <div className="flex justify-end gap-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleEditProduct(product)}
                                                                    >
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        onClick={() => {
                                                                            setProductToDelete(product);
                                                                            setDeleteDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Nom */}
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom du produit *</Label>
                            <Input
                                id="name"
                                value={form.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                placeholder="Ex: Poulet Braisé"
                            />
                        </div>
                        
                        {/* Catégorie et Prix */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Catégorie</Label>
                                <Select value={form.category} onValueChange={(v) => handleFormChange('category', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="price">Prix de vente (CFA) *</Label>
                                <Input
                                    id="price"
                                    type="number"
                                    value={form.price}
                                    onChange={(e) => handleFormChange('price', e.target.value)}
                                    placeholder="Ex: 3500"
                                />
                            </div>
                        </div>
                        
                        {/* Liaison Recette */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <ChefHat className="w-4 h-4 text-green-600" />
                                Lier à une recette (pour déduction auto des ingrédients)
                            </Label>
                            <Select 
                                value={form.linked_recipe_id || 'none'} 
                                onValueChange={(v) => handleFormChange('linked_recipe_id', v === 'none' ? '' : v)}
                            >
                                <SelectTrigger className={form.linked_recipe_id ? 'border-green-500' : ''}>
                                    <SelectValue placeholder="Aucune recette" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Aucune recette</SelectItem>
                                    {recipes.map(recipe => (
                                        <SelectItem key={recipe.id} value={recipe.id}>
                                            {recipe.name} - {formatCFA(recipe.sale_price_cfa)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {form.linked_recipe_id && (
                                <p className="text-xs text-green-600">
                                    ✓ Les ingrédients seront déduits automatiquement du stock à chaque vente
                                </p>
                            )}
                        </div>
                        
                        {/* Liaison Inventaire (si pas de recette) */}
                        {!form.linked_recipe_id && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <PackageCheck className="w-4 h-4 text-blue-600" />
                                    Ou lier directement à l'inventaire
                                </Label>
                                <Select 
                                    value={form.linked_product_id || 'none'} 
                                    onValueChange={(v) => handleFormChange('linked_product_id', v === 'none' ? '' : v)}
                                >
                                    <SelectTrigger className={form.linked_product_id ? 'border-blue-500' : ''}>
                                        <SelectValue placeholder="Aucun produit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Aucun produit</SelectItem>
                                        {inventoryProducts
                                            .filter(p => p.product_type === 'boisson' || p.category_name?.toLowerCase().includes('boisson'))
                                            .map(product => (
                                                <SelectItem key={product.id} value={product.id}>
                                                    {product.name} ({product.stock_display})
                                                </SelectItem>
                                            ))}
                                    </SelectContent>
                                </Select>
                                {form.linked_product_id && (
                                    <div className="space-y-2">
                                        <Label htmlFor="deduction">Quantité à déduire par vente</Label>
                                        <Input
                                            id="deduction"
                                            type="number"
                                            step="0.1"
                                            value={form.stock_deduction_qty}
                                            onChange={(e) => handleFormChange('stock_deduction_qty', e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Warning si non lié */}
                        {!form.linked_recipe_id && !form.linked_product_id && !editingProduct && (
                            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 text-blue-700">
                                    <PackageCheck className="w-5 h-5" />
                                    <span className="font-medium">Création automatique du stock</span>
                                </div>
                                <p className="text-xs text-blue-600">
                                    Un article sera automatiquement créé dans l'inventaire et lié à ce produit.
                                </p>
                                
                                {/* Stock initial et unité */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Stock initial</Label>
                                        <Input
                                            type="number"
                                            value={form.initial_stock}
                                            onChange={(e) => handleFormChange('initial_stock', e.target.value)}
                                            placeholder="0"
                                            className="h-9"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Unité</Label>
                                        <Select value={form.stock_unit} onValueChange={(v) => handleFormChange('stock_unit', v)}>
                                            <SelectTrigger className="h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="unité">Unité</SelectItem>
                                                <SelectItem value="bouteille">Bouteille</SelectItem>
                                                <SelectItem value="verre">Verre</SelectItem>
                                                <SelectItem value="portion">Portion</SelectItem>
                                                <SelectItem value="kg">Kg</SelectItem>
                                                <SelectItem value="litre">Litre</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                
                                {/* Seuil d'alerte et prix d'achat */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Seuil d'alerte</Label>
                                        <Input
                                            type="number"
                                            value={form.alert_threshold}
                                            onChange={(e) => handleFormChange('alert_threshold', e.target.value)}
                                            placeholder="5"
                                            className="h-9"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Prix d'achat (CFA)</Label>
                                        <Input
                                            type="number"
                                            value={form.purchase_price}
                                            onChange={(e) => handleFormChange('purchase_price', e.target.value)}
                                            placeholder="0"
                                            className="h-9"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Warning si non lié en édition */}
                        {!form.linked_recipe_id && !form.linked_product_id && editingProduct && (
                            <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg text-orange-700 text-sm">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                <p>Ce produit n'est pas lié à l'inventaire. Le stock ne sera pas déduit automatiquement.</p>
                            </div>
                        )}
                        
                        {/* Actif */}
                        <div className="flex items-center justify-between pt-2">
                            <Label>Produit actif</Label>
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(v) => handleFormChange('is_active', v)}
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveProduct} className="bg-green-600 hover:bg-green-700">
                            {editingProduct ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Le produit "{productToDelete?.name}" sera supprimé définitivement.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteProduct}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default MenuProductsPage;
